import { useEffect, useState } from "react";
import { ChevronDown, FileText, CreditCard, PiggyBank, Smartphone, Loader2, PenLine } from "lucide-react";
import { toast } from "sonner";
import { fetchMyInvoices, signInvoice, payInvoice, formatApiError } from "@/lib/api";
import InvoiceSheet from "@/components/InvoiceSheet";
import SignaturePad from "@/components/SignaturePad";

const STATUS_BADGE = {
  sent: "border-steel-brass/60 text-steel-brass",
  paid: "border-emerald-500/60 text-emerald-400",
  estimate: "border-amber-500/60 text-amber-400",
};

const PAYMENT_OPTIONS = [
  { id: "online", label: "Pay online", desc: "Card or electronic payment", Icon: CreditCard },
  { id: "refund", label: "Deduct from my refund", desc: "Fee comes out of your tax refund", Icon: PiggyBank },
  { id: "venmo", label: "Pay with Venmo", desc: "Send via Venmo after signing", Icon: Smartphone },
];

const METHOD_LABEL = {
  online: "pay online",
  refund: "have the fee deducted from your refund",
  venmo: "pay with Venmo",
};

function PaymentChoice({ invoice, onSigned }) {
  const [method, setMethod] = useState(null);
  const [sigData, setSigData] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!sigData) return toast.error("Draw your signature first.");
    if (name.trim().length < 2) return toast.error("Type your full name.");
    setSaving(true);
    try {
      const updated = await signInvoice(invoice.id, {
        payment_method: method,
        signer_name: name.trim(),
        signature_data: sigData,
      });
      if (method === "online") {
        toast.success("Signed — processing payment…");
        const res = await payInvoice(invoice.id, window.location.origin);
        if (res.paid) { toast.success("Paid in full with your account credit."); window.location.reload(); return; }
        window.location.href = res.checkout_url;
        return;
      }
      toast.success(`Signed — you chose to ${METHOD_LABEL[method]}. Steele Industries will confirm payment.`);
      onSigned(updated);
    } catch (e) {
      toast.error(formatApiError(e));
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-steel-border px-5 py-6" data-testid={`payment-choice-${invoice.id}`}>
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-steel-muted">
        How would you like to pay?
      </p>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {PAYMENT_OPTIONS.map(({ id, label, desc, Icon }) => (
          <button
            key={id}
            type="button"
            data-testid={`pay-option-${id}-${invoice.id}`}
            onClick={() => setMethod(id)}
            className={`border p-4 text-left transition-colors duration-300 ${
              method === id
                ? "border-steel-accent bg-steel-accent/10 shadow-[0_0_18px_rgba(147,51,234,0.25)]"
                : "border-steel-border bg-steel-elevated hover:border-steel-muted"
            }`}
          >
            <Icon className={`h-5 w-5 ${method === id ? "text-steel-brass" : "text-steel-muted"}`} />
            <p className={`mt-2 text-sm font-semibold ${method === id ? "text-steel-brass" : "text-steel-text"}`}>
              {label}
            </p>
            <p className="mt-1 text-xs text-steel-muted">{desc}</p>
          </button>
        ))}
      </div>

      {method && (
        <div className="mt-6 border border-steel-accent/40 bg-steel-elevated p-5" data-testid={`signature-block-${invoice.id}`}>
          <div className="flex items-center gap-3">
            <PenLine className="h-4 w-4 text-steel-brass" />
            <p className="text-sm text-steel-secondary">
              You chose to <span className="font-semibold text-steel-text">{METHOD_LABEL[method]}</span>. Sign
              below to authorize it — your signature is recorded on the invoice.
            </p>
          </div>
          <div className="mt-4">
            <SignaturePad onChange={setSigData} />
          </div>
          <input
            data-testid={`signer-name-${invoice.id}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-4 w-full border border-steel-border bg-steel-surface px-4 py-3 text-sm text-steel-text placeholder:text-steel-muted focus:border-steel-accent focus:outline-none"
            placeholder="Type your full legal name"
          />
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              data-testid={`sign-confirm-btn-${invoice.id}`}
              onClick={submit}
              disabled={saving || !sigData}
              className="flex items-center gap-2 bg-steel-accent px-6 py-3 text-sm font-semibold text-white shadow-[0_0_20px_rgba(168,85,247,0.30)] transition-colors duration-300 hover:bg-steel-accentHover disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />}
              Sign &amp; confirm
            </button>
            <button
              data-testid={`sign-cancel-btn-${invoice.id}`}
              onClick={() => { setMethod(null); setSigData(null); }}
              className="border border-steel-border px-6 py-3 text-sm text-steel-secondary transition-colors hover:text-steel-text"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PayNowCard({ invoice }) {
  const [loading, setLoading] = useState(false);
  const due = invoice.amount_due != null ? invoice.amount_due : invoice.total;
  const creditShown = invoice.credit_preview || invoice.credit_applied || 0;
  const start = async () => {
    setLoading(true);
    try {
      const res = await payInvoice(invoice.id, window.location.origin);
      if (res.paid) { toast.success("Paid in full with your account credit."); window.location.reload(); return; }
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(false);
    }
  };
  return (
    <div className="border-t border-steel-border px-5 py-6" data-testid={`pay-now-block-${invoice.id}`}>
      <p className="text-sm text-steel-secondary">
        You authorized card payment for this invoice. Complete it securely below.
      </p>
      {creditShown > 0 && (
        <p className="mt-2 text-sm text-steel-brass" data-testid={`credit-applied-${invoice.id}`}>
          Account credit applied: −${Number(creditShown).toLocaleString("en-US", { minimumFractionDigits: 2 })}
        </p>
      )}
      <button
        type="button"
        data-testid={`pay-now-btn-${invoice.id}`}
        onClick={start}
        disabled={loading}
        className="mt-4 flex items-center gap-2 bg-steel-accent px-6 py-3 text-sm font-semibold text-white shadow-[0_0_20px_rgba(168,85,247,0.30)] transition-colors duration-300 hover:bg-steel-accentHover disabled:opacity-50"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
        {due <= 0 ? "Apply credit & mark paid" : `Pay $${Number(due).toLocaleString("en-US", { minimumFractionDigits: 2 })} now`}
      </button>
    </div>
  );
}

export default function PortalInvoices() {
  const [invoices, setInvoices] = useState(null);
  const [credit, setCredit] = useState(0);
  const [openId, setOpenId] = useState(null);

  const load = () =>
    fetchMyInvoices()
      .then((d) => { setInvoices(d.invoices); setCredit(d.credit_balance || 0); })
      .catch(() => setInvoices([]));

  useEffect(() => {
    load();
  }, []);

  if (!invoices) return null;

  const onSigned = (updated) => {
    setInvoices((list) => list.map((i) => (i.id === updated.id ? updated : i)));
  };

  return (
    <div className="border border-steel-border bg-steel-surface p-8" data-testid="portal-invoices-card">
      <div className="flex items-center gap-4">
        <div className="flex h-11 w-11 items-center justify-center border border-steel-border">
          <FileText className="h-5 w-5 text-steel-brass" />
        </div>
        <div>
          <h3 className="font-display text-xl font-medium text-steel-text">Your invoices</h3>
          <p className="text-xs text-steel-muted">Estimates &amp; invoices from Steele Industries — no surprises, ever.</p>
        </div>
      </div>
      {credit > 0 && (
        <div className="mt-6 flex items-center gap-3 border border-steel-brass/40 bg-steel-brass/10 px-5 py-4" data-testid="portal-credit-banner">
          <PiggyBank className="h-5 w-5 shrink-0 text-steel-brass" />
          <p className="text-sm text-steel-text">
            You have <strong className="text-steel-brass">${Number(credit).toLocaleString("en-US", { minimumFractionDigits: 2 })}</strong> in account credit. It applies automatically to your next invoice.
          </p>
        </div>
      )}
      <div className="mt-6 space-y-3">
        {invoices.length === 0 && (
          <div className="flex flex-col items-start gap-3 border border-dashed border-steel-border p-8" data-testid="portal-invoices-empty">
            <FileText className="h-5 w-5 text-steel-muted" />
            <p className="text-sm text-steel-secondary">
              No invoices yet. When Steele Industries issues one, it will appear here for you to review and
              sign.
            </p>
          </div>
        )}
        {invoices.map((inv) => (
          <div key={inv.id} className="border border-steel-border bg-steel-elevated" data-testid={`portal-invoice-${inv.id}`}>
            <button
              type="button"
              data-testid={`portal-invoice-toggle-${inv.id}`}
              onClick={() => setOpenId(openId === inv.id ? null : inv.id)}
              className="flex w-full flex-wrap items-center justify-between gap-3 px-5 py-4 text-left"
            >
              <div className="flex items-center gap-4">
                <span className="font-mono text-sm font-semibold text-steel-text">{inv.number}</span>
                <span
                  className={`border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] ${STATUS_BADGE[inv.status] || "border-steel-border text-steel-muted"}`}
                  data-testid={`portal-invoice-status-${inv.id}`}
                >
                  {inv.status}
                </span>
                {inv.signed_at && (
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400" data-testid={`portal-invoice-signed-${inv.id}`}>
                    Signed
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4">
                <span className="font-mono text-sm text-steel-brass">
                  {inv.status === "estimate"
                    ? `$${Number(inv.estimate_min || 0).toLocaleString()} – $${Number(inv.estimate_max || inv.total).toLocaleString()}`
                    : `$${Number(inv.total).toLocaleString("en-US", { minimumFractionDigits: 2 })}`}
                </span>
                <ChevronDown
                  className={`h-4 w-4 text-steel-muted transition-transform duration-300 ${openId === inv.id ? "rotate-180" : ""}`}
                />
              </div>
            </button>
            {openId === inv.id && (
              <>
                <div className="border-t border-steel-border p-4 sm:p-6">
                  <InvoiceSheet invoice={inv} />
                </div>
                {inv.status === "estimate" && (
                  <div className="border-t border-steel-border px-5 py-5" data-testid={`portal-estimate-note-${inv.id}`}>
                    <p className="text-sm text-steel-secondary">
                      This is a preliminary estimate — there is nothing to pay or sign yet. Steele Industries will
                      send a formal invoice once your return is prepared.
                    </p>
                  </div>
                )}
                {inv.status === "sent" && !inv.signed_at && (
                  <PaymentChoice invoice={inv} onSigned={onSigned} />
                )}
                {inv.status === "sent" && inv.signed_at && inv.payment_method === "online" && (
                  <PayNowCard invoice={inv} />
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
